import React, { useEffect, useRef, useState } from 'react';
import { Modal, Box, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel, Autocomplete, Grid, ListItemText, Chip, IconButton } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';
import AddResourceModal from './AddResourceModal';

function ScheduleSessionModal(props) {
  const [scheduleName, setScheduleName] = useState('');
  const [resource, setResource] = useState('');
  const [resourceName, setResourceName] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date());
  const [maxCapacity, setMaxCapacity] = useState('');
  const [notes, setNotes] = useState('');
  const [taskType, setTaskType] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  // Customers selection (optional at creation, but if provided sessions will be auto-created)
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [customersOpen, setCustomersOpen] = useState(false);
  const assignAllOption = { cus_id: 'assign_all', cus_name: 'Assign All' };
  const [preAuthList, setPreAuthList] = useState([]); // only used for day care remaining units display
  const [isAddResourceModalOpen, setIsAddResourceModalOpen] = useState(false);

  const handleOpenAddResourceModal = () => setIsAddResourceModalOpen(true);
  const handleCloseAddResourceModal = () => setIsAddResourceModalOpen(false);

  const [errors, setErrors] = useState({
    scheduleName: false,
    resource: false,
    employees: false,
    selectedDate: false,
    startTime: false,
    endTime: false,
    maxCapacity: false,
    taskType: false,
    customers: false,
  });

  const validate = () => {
    const newErrors = {
      scheduleName: !scheduleName,
      resource: !resource,
      employees: employees?.length === 0,
      selectedDate: !selectedDate,
      startTime: !startTime || moment(startTime).isSameOrAfter(endTime),
      endTime: !endTime || moment(endTime).isSameOrBefore(startTime),
      maxCapacity: !maxCapacity || maxCapacity <= 0,
      taskType: !taskType,
      customers: false // optional for creation; kept for future rule toggles
    };
    setErrors(newErrors);
    setSubmitted(true);
    return Object.values(newErrors).every(error => !error);
  };

  const handleScheduleNameChange = (event) => {
    setScheduleName(event.target.value);
  };

  const handleResourceChange = (event, newValue) => {    
    if (newValue && newValue.resource_id === 'ADD_RESOURCE_OPTION') {      
      handleOpenAddResourceModal();
      setResource(''); // Ensure the "Add New Resource" option is not selected
      setMaxCapacity(''); // Clear the max capacity
      event.target.value = ''; // Clear the input value
    } else {
      setResource(newValue);
      setMaxCapacity(newValue?.capacity || '');
      setErrors({ ...errors, resource: false, maxCapacity: newValue?.capacity <= 0 });
    }
  };

  const handleResourceInputChange = (event, newInputValue) => {
    console.log(newInputValue);
    
    if (newInputValue === '' || newInputValue === `+ Add New ${resourceLabelSingle}`) {
      setResourceName('');
    } else {
      setResourceName(newInputValue);
    }
  };

  const handleEmployeesChange = (event, newValue) => {
    if (props.user.permissions.ScheduleSessionForOtherEmployees) {
      setEmployees(newValue);
    } else {
      setEmployees(newValue ? [newValue] : []);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // A ref blocks rapid submissions even before React renders the disabled button.
    if (isSubmittingRef.current) {
      return;
    }

    if (!validate()) {
      return;
    }

    let date = moment(selectedDate);
    // Capacity check if customers chosen
    if (selectedCustomers.length > 0 && Number(maxCapacity) && selectedCustomers.length > Number(maxCapacity)) {
      props.onError(`The number of ${props.isDayCare ? 'Members' : 'Customers'} exceeds the maximum capacity`);
      return;
    }
    let session = { 
      schedule_name: scheduleName,
      resource_id: resource.resource_id,
      employee_ids: employees.map(emp => emp.id),
      date: date.format('YYYY-MM-DD'),
      start_time: moment(startTime).set({ year: date.year(), month: date.month(), date: date.date() }).utc().format('YYYY-MM-DDTHH:mm:00'),
      end_time: moment(endTime).set({ year: date.year(), month: date.month(), date: date.date() }).utc().format('YYYY-MM-DDTHH:mm:00'),
      max_capacity: maxCapacity,
      task_type: taskType,
      notes: notes ? notes : null,
      company_id: props.comp_id,
      created_by: props.user.emp_id,
      customers: selectedCustomers.map(c => c.cus_id),
    };
    
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      await axios.post(APIURL + '/master_schedule', session);
      props.reloadScheduleData();
      props.onClose();
      props.onSuccess("Session scheduled successfully");
    } catch (error) {
      if (error.response) {
        if (error.response.status === 400) {
          props.onError(error.response.data.message);
        } else {
          props.onError("An error occurred while scheduling session");
        }
      } else {
        props.onError("An error occurred while scheduling session");
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleClose = (event, reason) => {
    if (reason !== 'backdropClick' && !isSubmittingRef.current) {
      props.onClose();
    }
  };

  useEffect(() => {
    if(!props.open) {
      setScheduleName('');
      setResource('');
      setEmployees([]);
      setSelectedDate(new Date());
      setStartTime(new Date());
      setEndTime(new Date());
      setMaxCapacity('');
      setNotes('');
      setTaskType('');
      setSubmitted(false);
      setSelectedCustomers([]);
      setErrors({
        resource: false,
        employee: false,
        selectedDate: false,
        startTime: false,
        endTime: false,
        maxCapacity: false,
        taskType: false,
        customers: false,
      });
    } else {
      const defaultEmployee = props.employees?.find((employee) => employee.id === props.user.emp_id);
      setEmployees(defaultEmployee ? [defaultEmployee] : []);
    }
  }, [props.open]);
  // PreAuth list for day care customer remaining units (task type dependent)
  useEffect(() => {
    if (props.isDayCare && taskType) {
      axios.get(APIURL + '/preAuth/taskType', { 
        params: { 
          companyID: props.comp_id, 
          taskType, 
          fromDate: moment(startTime).isValid() ? moment(startTime).subtract(2, 'months').format('MM/DD/YYYY') : null,
          toDate: moment(startTime).isValid() ? moment(startTime).endOf("day").format('MM/DD/YYYY') : null
        } 
      })
        .then(response => setPreAuthList(response.data.sort((a, b) => new Date(b.startDate) - new Date(a.startDate))))
        .catch(() => setPreAuthList([]));
    } else {
      setPreAuthList([]);
    }
  }, [taskType, props.isDayCare]);

  const customerLabelPlural = props.isDayCare ? 'Members' : 'Customers';

  const renderCustomerOption = (propsOption, option) => {
    const preAuth = preAuthList.find(item => item.cus_id === option.cus_id);
    const remainingUnits = preAuth ? Math.max(0, preAuth.remainingUnits) : null;
    const unitString = remainingUnits !== null ? `${remainingUnits}${remainingUnits === 1 ? ' Unit' : ' Units'}` : '';
    return (
      <li {...propsOption} key={option.cus_id}>
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {option.cus_id === 'assign_all' ? (
            <span style={{ color: '#3fa7d6', cursor: 'pointer' }}>Select All</span>
          ) : (
            <>
              <span>{option.cus_name}</span>
              {option.cus_status === 'Prospect' && (
                <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'gray' }}>Prospect</span>
              )}
              {unitString && <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'gray' }}>{unitString}</span>}
            </>
          )}
        </div>
      </li>
    );
  };

  useEffect(() => {
    if (props.slotScheduleInfo) {
      props.slotScheduleInfo.startTime ? setStartTime(props.slotScheduleInfo.startTime) : setStartTime(new Date());
      props.slotScheduleInfo.endTime ? setEndTime(props.slotScheduleInfo.endTime) : setEndTime(new Date());
      props.slotScheduleInfo.startTime ? setSelectedDate(props.slotScheduleInfo.startTime) : setSelectedDate(new Date());
      if (props.slotScheduleInfo.resource) {
        setResource(props.slotScheduleInfo.resource);
        setMaxCapacity(props.slotScheduleInfo.resource.capacity);
      } else {
        setResource('');
        setMaxCapacity('');
      }
    }
  }, [props.slotScheduleInfo]);

  const resourceLabelSingle = props.isDayCare ? 'Location' : 'Resource';
  const resourceLabelPlural = props.isDayCare ? 'Locations' : 'Resources';

  const newResourceOption = {
    resource_id: 'ADD_RESOURCE_OPTION',
    resource_name: `+ Add New ${resourceLabelSingle}`,
  };

  const handleAddResource = (newResource) => {
    props.resources.push(newResource);
    setResource(newResource);
    setMaxCapacity(newResource.capacity || '');
    setIsAddResourceModalOpen(false);
  };

  return (
    <Modal enforceFocus={false}
      open={props.open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{ display: 'flex', justifyContent: 'center', overflowY: 'auto', p: 2 }}
    >
      <Box sx={{ width: 500, maxWidth: '100%', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2, height: 'fit-content' }}>
        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Schedule New Session
          </Typography>
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{ p: 0.25 }}
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            label="Session Name"
            fullWidth
            margin="normal"
            value={scheduleName}
            onChange={handleScheduleNameChange}
            error={errors.scheduleName && submitted}
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
          <FormControl fullWidth margin="normal">
            <Autocomplete
              inputValue={resourceName}
              options={[...props.resources, newResourceOption]}
              getOptionLabel={(option) => option.resource_name}
              renderOption={(props, option) => {
                if (option.resource_id === 'ADD_RESOURCE_OPTION') {
                  return (
                    <li {...props} style={{ color: '#3fa7d6' }}>
                      {option.resource_name}
                    </li>
                  );
                }
                return (
                  <li {...props}>
                    {option.resource_name}
                  </li>
                );
              }}
              onChange={handleResourceChange}
              onInputChange={handleResourceInputChange}
              renderInput={(params) => <TextField {...params} label={resourceLabelSingle} error={errors.resource && submitted} />}
            />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel id="task-type-select-label">Task Type</InputLabel>
            <Select
              labelId="task-type-select-label"
              value={taskType}
              onChange={(e) => setTaskType(e.target.value)}
              error={errors.taskType && submitted}
            >
              {props.taskTypes.map((taskType, index) => (
                <MenuItem key={index} value={taskType}>
                  {taskType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <Autocomplete
              multiple={props.user.permissions.ScheduleSessionForOtherEmployees}
              id="employee-select"
              options={props.employees}
              getOptionLabel={(option) => option.employee_name}
              renderInput={(params) => <TextField {...params} label={props.user.permissions.ScheduleSessionForOtherEmployees ? "Employees" : "Employee"} error={errors.employees && submitted} />}
              onChange={handleEmployeesChange}
              value={props.user.permissions.ScheduleSessionForOtherEmployees ? employees : employees[0] || null}
              disabled={!props.user.permissions.ScheduleSessionForOtherEmployees && employees.length >= 1}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    <ListItemText primary={option.employee_name} />
                  </li>
                )
              }}
            />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <Autocomplete
              multiple
              id="customer-select"
              options={[assignAllOption, ...(props.customers || []).slice().sort((a, b) =>
                (a.cus_name || '').localeCompare(b.cus_name || '', undefined, { sensitivity: 'base' })
              )]}
              value={selectedCustomers}
              getOptionLabel={(option) => option.cus_name}
              filterSelectedOptions
              open={customersOpen}
              onOpen={() => setCustomersOpen(true)}
              onClose={(event, reason) => {
                if (reason === 'selectOption' && event.target?.innerText !== 'Select All') {
                  setCustomersOpen(true);
                } else {
                  setCustomersOpen(false);
                }
              }}
              onChange={(event, newValue) => {
                if (newValue.some(item => item.cus_id === 'assign_all')) {
                  setSelectedCustomers((props.customers || []));
                } else {
                  setSelectedCustomers(newValue.filter(item => item.cus_id !== 'assign_all'));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={`Select ${customerLabelPlural}`}
                  placeholder={customerLabelPlural}
                  error={errors.customers && submitted}
                />
              )}
              renderValue={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option.cus_id} label={option.cus_name} />
                ))
              }
              renderOption={renderCustomerOption}
            />
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mt: 2, mb: 2 }}> {/* Adjusted spacing here */}
              <Grid container spacing={2} alignItems="center">
                <Grid size={8}>
                  <DatePicker
                    label="Select Date"
                    value={moment(selectedDate).isValid() ? new Date(selectedDate) : null}
                    onChange={setSelectedDate}
                    slotProps={{ textField: { fullWidth: true } }}
                    error={errors.selectedDate && submitted}
                    
                  />
                </Grid>
                <Grid size={4}>
                  <TextField
                    label="Max Capacity"
                    type="number"
                    value={maxCapacity}
                    onChange={(e) => setMaxCapacity(e.target.value)}
                    fullWidth
                    slotProps={{ htmlInput: { max: resource?.capacity || '' } }}
                    error={errors.maxCapacity && submitted}
                  />
                </Grid>
              </Grid>
            </Box>
            <Grid container spacing={2} alignItems="center">
              <Grid size={6}>
                <TimePicker
                  label="Start Time"
                  value={startTime}
                  onChange={setStartTime}
                  slotProps={{ textField: { fullWidth: true, error: errors.startTime && submitted } }}
                />
              </Grid>
              <Grid size={6}>
                <TimePicker
                  label="End Time"
                  value={endTime}
                  onChange={setEndTime}
                  slotProps={{ textField: { fullWidth: true, error: errors.startTime && submitted } }}
                  error={errors.endTime && submitted}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
          <TextField
            margin="normal"
            fullWidth
            label="Notes"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
          <Button type="submit" fullWidth variant="contained" disabled={isSubmitting} sx={{ mt: 3, mb: 2 }}>
            {isSubmitting ? 'Scheduling…' : 'Schedule Session'}
          </Button>
        </Box>
          {isAddResourceModalOpen && (
            <AddResourceModal
              open={isAddResourceModalOpen}
              onClose={handleCloseAddResourceModal}
              locations={props.locations} // Pass locations prop
              contacts={props.contacts} // Pass contacts prop
              isDayCare={props.isDayCare} // Pass isDayCare prop
              onAddResource={handleAddResource} // Pass handler for adding resource
            />
          )}
      </Box>
    </Modal>
  );
}

export default ScheduleSessionModal;
